import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fileUrl } from "../lib/seo";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

export function PhotoViewer({ photos, idx, setIdx, caption }) {
  if (idx === null) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && setIdx(null)}>
      <DialogContent data-testid="impact-lightbox" className="max-w-4xl bg-[#1A0508] border-[#D4AF37]/40 text-[#FAF6F0] p-0 overflow-hidden">
        <DialogTitle className="sr-only">Impact photo</DialogTitle>
        <div className="relative bg-black flex items-center justify-center h-[60vh]">
          <img src={fileUrl(photos[idx])} alt="" className="max-h-full max-w-full object-contain" />
          <button data-testid="impact-prev" disabled={idx === 0} onClick={() => setIdx(idx - 1)} className="absolute left-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center disabled:opacity-20"><ChevronLeft className="w-5 h-5" /></button>
          <button data-testid="impact-next" disabled={idx === photos.length - 1} onClick={() => setIdx(idx + 1)} className="absolute right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center disabled:opacity-20"><ChevronRight className="w-5 h-5" /></button>
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-xs bg-black/50 px-2 py-0.5 rounded">{idx + 1} / {photos.length}</span>
        </div>
        {caption && <div className="p-5 text-sm text-[#FAF6F0]/85">{caption}</div>}
      </DialogContent>
    </Dialog>
  );
}

function ReportCard({ r }) {
  const [idx, setIdx] = useState(null);
  return (
    <article data-testid={`impact-report-${r.id}`} className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
      <div className={`grid gap-1 ${{ 1: "grid-cols-1", 2: "grid-cols-2" }[r.photo_urls.length] || "grid-cols-3"}`}>
        {r.photo_urls.slice(0, 3).map((u, i) => (
          <button key={u} onClick={() => setIdx(i)} data-testid={`impact-photo-${r.id}-${i}`} className={`relative overflow-hidden ${r.photo_urls.length > 1 ? "aspect-square" : "aspect-[16/9]"}`}>
            <img src={fileUrl(u)} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            {i === 2 && r.photo_urls.length > 3 && <span className="absolute inset-0 bg-black/50 text-white font-semibold flex items-center justify-center">+{r.photo_urls.length - 3}</span>}
          </button>
        ))}
      </div>
      <div className="p-5">
        <div className="overline !text-[0.6rem]">{r.ngo_name} · {fmt(r.reviewed_at || r.created_at)}</div>
        <h3 className="font-serif text-xl text-[#4A0E17] mt-1">{r.title}</h3>
        <div className="text-xs text-[#8C6D3B] mt-1">Project: {r.project_title}{r.amount_granted_usd > 0 ? ` · $${r.amount_granted_usd.toLocaleString()} granted` : ""}</div>
        <p className="text-sm text-[#63534E] mt-3 line-clamp-4">{r.body}</p>
      </div>
      <PhotoViewer photos={r.photo_urls} idx={idx} setIdx={setIdx} caption={`${r.title} — ${r.ngo_name}`} />
    </article>
  );
}

export default function ImpactReportsSection() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/impact-reports").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (!items) return null;
  return (
    <section data-testid="impact-reports-section" className="mt-16">
      <div className="overline">Impact on the ground</div>
      <h2 className="font-serif text-3xl md:text-4xl text-[#4A0E17] mt-2">Updates from our NGO partners</h2>
      <p className="text-[#63534E] mt-3 max-w-2xl">Photos and progress notes posted by verified NGOs and checked by our team before they appear here.</p>
      {items.length === 0 ? <p data-testid="impact-empty" className="mt-6 text-sm text-[#63534E]">No updates yet — the first stories from funded projects will appear here.</p> : (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((r) => <ReportCard key={r.id} r={r} />)}</div>
      )}
    </section>
  );
}
